import { baseApi } from "./baseApi";
import { MediaDto } from "../../interfaces/media";
import {
  CreateLectureRequest,
  LectureDto,
  UpdateLectureRequest,
} from "../../interfaces";
import axios from "axios";

export const lectureApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLecturesByLesson: builder.query<LectureDto[], string>({
      query: (lessonId) => ({ url: `/api/lecture/${lessonId}/lesson` }),
      providesTags: ["Lectures-By-Lesson"],
    }),
    getLecture: builder.query<LectureDto, string>({
      query: (id) => ({ url: `/api/lecture/${id}` }),
      providesTags: ["Lecture"],
    }),
    addLecture: builder.mutation<any, CreateLectureRequest>({
      query: (data) => ({
        url: "/api/lecture",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Lectures-By-Lesson"],
    }),
    updateLecture: builder.mutation<
      any,
      { id: string; data: UpdateLectureRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/lecture/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Lecture", "Lectures-By-Lesson"],
    }),
    deleteLecture: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/lecture/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Lectures-By-Lesson", "Chapter-Lecture-Stats"],
    }),
    toggleLectureState: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/lecture/toggle-state/${id}`,
        method: "PATCH",
        body: {},
      }),
      invalidatesTags: ["Lecture", "Lectures-By-Lesson"],
    }),
    uploadLectureVideo: builder.mutation<
      MediaDto,
      {
        file: File;
        lectureId?: string | null;
        onProgress?: (progress: number) => void;
      }
    >({
      async queryFn(
        { file, lectureId, onProgress },
        api,
        _extraOptions,
        baseQuery,
      ) {
        try {
          onProgress?.(1);

          const signedUrlResult: any = await baseQuery({
            url: `/api/v2/lecture/video-upload-url`,
            method: "POST",
            body: {
              fileName: file.name,
              fileType: file.type,
            },
          });

          if (signedUrlResult.error) {
            return { error: signedUrlResult.error };
          }

          const { url, key } = signedUrlResult.data as {
            url: string;
            key: string;
          };

          onProgress?.(2);

          await axios.put(url, file, {
            headers: {
              "Content-Type": file.type,
            },
            onUploadProgress: (event) => {
              if (event.total && onProgress) {
                // Reserve the last stretch for upload-complete so the bar doesn't sit at 100% early.
                const pct = Math.min(
                  95,
                  Math.round((event.loaded / event.total) * 93) + 2,
                );
                onProgress(pct);
              }
            },
          });

          onProgress?.(97);

          const completeResult: any = await baseQuery({
            url: lectureId
              ? `/api/v2/lecture/upload-complete?id=${lectureId}`
              : `/api/v2/lecture/upload-complete`,
            method: "POST",
            body: { key, name: file.name },
          });

          if (completeResult.error) {
            return { error: completeResult.error };
          }

          onProgress?.(100);

          return { data: completeResult.data as MediaDto };
        } catch (error: any) {
          return {
            error: {
              status: error?.response?.status || 500,
              data: error?.message,
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetLecturesByLessonQuery,
  useGetLectureQuery,
  useAddLectureMutation,
  useUpdateLectureMutation,
  useDeleteLectureMutation,
  useToggleLectureStateMutation,
  useLazyGetLectureQuery,
  useUploadLectureVideoMutation,
} = lectureApi;
